import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  CheckCircle2, Truck, Building2, Briefcase, Sparkles, ShieldAlert,
  Loader2, ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';
import WaitlistModal from '@/components/WaitlistModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState(null);
  const [stripeLive, setStripeLive] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loadingTier, setLoadingTier] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistType, setWaitlistType] = useState('carrier');
  const [waitlistTrigger, setWaitlistTrigger] = useState('');

  const openWaitlist = (tier) => {
    setWaitlistType(tier?.segment === 'broker' ? 'broker'
                  : tier?.segment === 'shipper' ? 'shipper' : 'carrier');
    setWaitlistTrigger(tier?.name || 'this tier');
    setWaitlistOpen(true);
  };

  useEffect(() => {
    axios.get(`${API}/subscriptions/tiers`)
      .then((r) => { setTiers(r.data); setStripeLive(!!r.data.stripe_live); })
      .catch(() => toast.error('Failed to load pricing'));
  }, []);

  const checkout = async (tier) => {
    if (!email.trim()) {
      toast.error('Please enter your email above to continue');
      return;
    }
    if (tier.contact_only) {
      window.location.href = `mailto:givi@tempusdispatch.com?subject=Enterprise%20inquiry%20—%20${encodeURIComponent(tier.name)}`;
      return;
    }
    setLoadingTier(tier.id);
    try {
      // Free-trial-eligible tiers go through the trial endpoint instead of
      // Stripe checkout — no credit card required upfront.
      if (tier.free_trial_days && tier.free_trial_days > 0) {
        const r = await axios.post(`${API}/subscriptions/trial/start`, {
          email, name, tier: tier.id,
        });
        try { localStorage.setItem('trialBannerEmail', email.toLowerCase()); } catch {}
        if (r.data.status === 'already_in_trial') {
          toast.info(`You're already in a ${tier.name} trial — ends ${new Date(r.data.trial_ends_at).toLocaleDateString()}`);
        } else {
          toast.success(`Your ${tier.free_trial_days}-day free trial is live. Check your email for setup instructions.`);
        }
        return;
      }
      let r;
      if (tier.model === 'usage_based') {
        r = await axios.post(`${API}/subscriptions/carrier/enroll`, {
          email, name, tier: tier.id,
          origin_url: window.location.origin,
        });
        toast.success('Enrolled — submit your monthly revenue when ready');
      } else {
        r = await axios.post(`${API}/subscriptions/checkout`, {
          email, name, tier: tier.id,
          origin_url: window.location.origin,
        });
        if (r.data.checkout_url) {
          window.location.href = r.data.checkout_url;
          return;
        }
        toast.success(`Activated ${tier.name}`);
      }
    } catch (e) {
      if (e.response?.status === 503) {
        toast.warning('Coming soon — Stripe live keys not yet configured');
      } else {
        toast.error(e.response?.data?.detail || 'Checkout failed');
      }
    } finally {
      setLoadingTier(null);
    }
  };

  if (!tiers) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-zinc-950 text-zinc-100">
      <PlatformDisclaimer />
      <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30 mb-3" data-testid="pricing-locked-badge">
            <Sparkles className="w-3 h-3 mr-1" /> Locked pricing · final model
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">
            Hire an <span className="text-amber-400">AI dispatcher</span>.<br />
            Pay only when you earn.
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 mt-3 max-w-2xl mx-auto">
            Tempus is your authorized dispatch agent. We book your loads, send rate confirmations, and chase backup coverage — for 6% of your gross. Less than any human dispatcher.
          </p>
        </div>

        {/* Email gate */}
        <div className="max-w-md mx-auto mb-12 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
                 data-testid="pricing-name-input" className="bg-zinc-900/60 border-zinc-700" />
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email"
                 data-testid="pricing-email-input" className="bg-zinc-900/60 border-zinc-700" />
        </div>

        {/* CARRIER DISPATCH SERVICE — HERO PRODUCT */}
        <Section icon={Truck} title="Carrier Dispatch Service" subtitle="The product. Your AI dispatcher. Replaces the 7% human dispatcher.">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
            {tiers.carrier_dispatch.map((t) => (
              <TierCard
                key={t.id} tier={t} stripeLive={stripeLive} loadingTier={loadingTier}
                onCheckout={checkout} onWaitlist={openWaitlist} highlight
                ctaLabel="Hire Tempus as my dispatcher"
                priceDisplay={<><span className="text-5xl font-bold tabular-nums">6%</span>
                                <span className="text-base text-zinc-400 ml-2">of gross monthly freight revenue</span></>}
              />
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-3 max-w-2xl italic">
            6% dispatch service fee charged to carriers — Tempus acts as your authorized dispatch agent under the Carrier Dispatch Agreement. Tempus is not a freight broker.
          </p>
        </Section>

        {/* Divider — separate audiences below */}
        <div className="my-14 border-t border-zinc-800 pt-10 text-center">
          <p className="text-xs uppercase tracking-widest text-zinc-500">
            Not a carrier? Tempus also offers SaaS tools for brokers and platform access for shippers
          </p>
        </div>

        {/* BROKER SAAS */}
        <Section icon={Briefcase} title="Broker SaaS" subtitle="For freight brokers · flat monthly subscription">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {tiers.broker_saas.map((t) => (
              <TierCard key={t.id} tier={t} stripeLive={stripeLive} loadingTier={loadingTier}
                        onCheckout={checkout} onWaitlist={openWaitlist} highlight={t.popular}
                        priceDisplay={t.monthly_amount === 0
                          ? <span className="text-5xl font-bold">Free</span>
                          : <><span className="text-5xl font-bold tabular-nums">${t.monthly_amount}</span>
                              <span className="text-base text-zinc-400 ml-1">/mo</span></>}
                        ctaLabel={t.monthly_amount === 0
                          ? 'Start free'
                          : t.free_trial_days
                            ? `Start ${t.free_trial_days}-day free trial`
                            : `Subscribe — $${t.monthly_amount}/mo`} />
            ))}
          </div>
        </Section>

        {/* SHIPPER PLATFORM ACCESS */}
        <Section icon={Building2} title="Shipper Platform Access" subtitle="For shippers and enterprises moving real freight volume">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {tiers.shipper_platform.map((t) => (
              <TierCard key={t.id} tier={t} stripeLive={stripeLive} loadingTier={loadingTier}
                        onCheckout={checkout} onWaitlist={openWaitlist} highlight={t.popular}
                        priceDisplay={t.contact_only
                          ? <><span className="text-3xl font-bold">Contact us</span>
                              <span className="text-sm text-zinc-400 block mt-1">from ${(t.min_amount || 10000).toLocaleString()}/mo</span></>
                          : <><span className="text-5xl font-bold tabular-nums">${t.monthly_amount.toLocaleString()}</span>
                              <span className="text-base text-zinc-400 ml-1">/mo</span></>}
                        ctaLabel={t.contact_only
                          ? 'Contact sales'
                          : t.free_trial_days
                            ? `Start ${t.free_trial_days}-day free trial`
                            : `Subscribe — $${t.monthly_amount.toLocaleString()}/mo`} />
            ))}
          </div>
        </Section>

        <div className="mt-12 p-4 rounded-lg border border-amber-500/30 bg-amber-500/5 text-amber-200/90 text-sm flex items-start gap-3 max-w-4xl mx-auto" data-testid="pricing-legal-block">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <strong>Tempus Dispatch LLC is a SaaS platform and carrier dispatch agent. Not a freight broker. Not liable for freight transactions. (49 USC § 13904)</strong>
            <br/>
            Tempus does not hold FMCSA Broker Authority and is not a party to any freight contract.
            See <a href="/terms" className="underline">Terms of Service</a> for the Carrier Commitment Policy and Re-dispatch Guarantee.
          </span>
        </div>
      </div>
      <WaitlistModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        defaultType={waitlistType}
        triggerLabel={waitlistTrigger}
      />
    </div>
  );
};

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-5 h-5 text-blue-400" />
      <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{title}</h2>
    </div>
    {subtitle && <p className="text-sm text-zinc-400 mb-5">{subtitle}</p>}
    {children}
  </div>
);

const TierCard = ({ tier, stripeLive, loadingTier, onCheckout, onWaitlist, highlight, priceDisplay, ctaLabel }) => {
  const hasTrial = !!tier.free_trial_days && tier.free_trial_days > 0;
  // Trial tiers don't need Stripe live to start (no card collected up-front).
  const requiresStripe = !tier.contact_only && (tier.monthly_amount || 0) > 0 && tier.model !== 'usage_based' && !hasTrial;
  const checkoutDisabled = requiresStripe && !stripeLive;
  return (
    <Card className={`bg-zinc-900/60 border ${highlight ? 'border-blue-500/50 ring-1 ring-blue-500/20' : 'border-zinc-800'} relative`}
          data-testid={`tier-card-${tier.id}`}>
      {tier.popular && (
        <Badge className="absolute -top-2 left-4 bg-blue-500 text-white border-blue-400">Most popular</Badge>
      )}
      {hasTrial && (
        <Badge
          className="absolute -top-2 right-4 bg-emerald-500/90 text-white border-emerald-400"
          data-testid={`trial-badge-${tier.id}`}
        >
          {tier.free_trial_days}-day free trial
        </Badge>
      )}
      <CardContent className="p-6">
        <div className="font-medium text-zinc-300">{tier.name}</div>
        <div className="mt-2 mb-4">
          {priceDisplay}
        </div>
        {hasTrial && (
          <p className="text-[12px] text-emerald-300 mb-3 -mt-2" data-testid={`trial-copy-${tier.id}`}>
            ✓ Start free for {tier.free_trial_days} days — no credit card required.
            Billing begins automatically only after you add a card.
          </p>
        )}
        {tier.tagline && <p className="text-sm text-zinc-400 mb-4">{tier.tagline}</p>}
        <ul className="space-y-1.5 mb-6">
          {(tier.features || []).map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {checkoutDisabled ? (
          <Button
            className="w-full"
            onClick={() => onWaitlist?.(tier)}
            variant={highlight ? 'default' : 'outline'}
            data-testid={`reserve-spot-${tier.id}-btn`}
          >
            <Sparkles className="w-4 h-4 mr-2" /> Reserve Your Spot
          </Button>
        ) : (
          <Button
            className="w-full"
            disabled={loadingTier === tier.id}
            onClick={() => onCheckout(tier)}
            variant={highlight ? 'default' : 'outline'}
            data-testid={`subscribe-${tier.id}-btn`}
          >
            {loadingTier === tier.id ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</>
            ) : (
              <>{ctaLabel} <ArrowRight className="w-3.5 h-3.5 ml-1" /></>
            )}
          </Button>
        )}
        {checkoutDisabled && (
          <p className="text-[11px] text-amber-400 mt-2 text-center" data-testid={`coming-soon-${tier.id}`}>
            Pre-launch — joining the waitlist locks in founding-customer pricing.
          </p>
        )}
        {tier.legal_note && (
          <p className="text-[10px] text-zinc-500 mt-3 italic">{tier.legal_note}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default PricingPage;
